import {
  AspectRatio,
  Box,
  HStack,
  Link,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import PampaImage from "@raiden/library/components/PampaImage";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { FormattedMessage } from "react-intl";
import { Href } from "../../Href";
import { Icon } from "../../Icon";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 */
/**
 * @param {Props} props
 */
export function CommunityHallsCard({ rentalArea }) {
  const translate = useTranslate();

  const format =
    useBreakpointValue({ base: "default", lg: "large" }) ?? "default";

  return (
    <Box flexGrow={1} maxW="380px" bg="gray.50" borderRadius="4px">
      <Href w="full" href={`/salles-communales#rentalArea-${rentalArea.id}`}>
        <HStack
          align="start"
          borderRadius="4px"
          transition="all 0.25s ease"
          _hover={{
            bg: "brandPrimary.100",
          }}>
          <AspectRatio ratio={16 / 9} w="117px" h="117px">
            <PampaImage
              image={rentalArea?.covers?.[0]}
              alt={translate(rentalArea?.covers?.[0]?.caption) ?? ""}
              format={format}
              borderRadius="4px"
              mode="crop"
              placeholderSrc={generateFrontPath({
                id: "@front.internal-assets",
                parameters: { filePath: "placeholder.svg" },
                includeBasePath: true,
              })}
              _groupHover={{
                transform: "scale(1.1)",
              }}
              transition="all 0.25s ease"
              bg="gray.100"
            />
          </AspectRatio>

          <VStack pt=".5rem" align="start">
            <Text fontSize="1.25rem" fontWeight={400}>
              {translate(rentalArea.name)}
            </Text>

            <HStack align="start">
              <Icon icon="ms_place" size="1.25rem" color="gray.400" />

              <Box>
                <Text color="gray.600">
                  {rentalArea?.lat && rentalArea?.lng ? (
                    <Link
                      href={`https://www.google.com/maps/search/?api=1&query=${rentalArea.lat},${rentalArea.lng}`}
                      textDecoration="underline"
                      isExternal>
                      <FormattedMessage
                        defaultMessage={"{address}"}
                        values={{
                          address: rentalArea?.address2
                            ? `${rentalArea.address1}, ${rentalArea.address2}`
                            : rentalArea.address1,
                        }}
                      />
                    </Link>
                  ) : (
                    <FormattedMessage
                      defaultMessage={"{address}"}
                      values={{
                        address: rentalArea?.address2
                          ? `${rentalArea.address1}, ${rentalArea.address2}`
                          : rentalArea.address1,
                      }}
                    />
                  )}
                </Text>
              </Box>
            </HStack>

            {rentalArea.phone_country && rentalArea.phone_number && (
              <HStack align="start">
                <Icon icon="ms_phone" size="1.25rem" color="gray.400" />

                <Text color="gray.600">
                  <Link href={`tel:${rentalArea.phone_number}`}>
                    {rentalArea.phone_number
                      .replace(/^\+33/, "0")
                      .replace(/(\d{2})(?=\d)/g, "$1 ")}
                  </Link>
                </Text>
              </HStack>
            )}

            {rentalArea.website && (
              <HStack align="start">
                <Icon icon="ms_info" size="1.25rem" color="gray.400" />

                <Text color="gray.600">
                  <Link href={`https://${rentalArea.website}`} isExternal>
                    {rentalArea.website}
                  </Link>
                </Text>
              </HStack>
            )}
          </VStack>
        </HStack>
      </Href>
    </Box>
  );
}
